"use client"
import { Image, type ImageProps, Video } from "@opensea/next-ui-kit"
import type { MediaFormat } from "@opensea/seadn"
import {
  classNames,
  extractMediaVariant,
  Media,
  type VideoFormat,
} from "@opensea/ui-kit"
import type { Nullable } from "@opensea/utils"
import type React from "react"
import { useEffect, useRef } from "react"
import { extractMediaVariantFromDataUri } from "@/lib/upload"

export type BannerMediaProps = {
  className?: string
  mediaClassName?: string
  overlay?: React.ReactNode
  blur?: boolean
  url?: Nullable<string>
  fallback?: Nullable<string>
  priority?: boolean
  height?: number
  width?: number
  onLoad?: () => void
  format?: MediaFormat
  disableVideo?: boolean
} & Pick<ImageProps, "frameTime" | "fill" | "sizes" | "boost">

function BannerMedia({
  overlay,
  blur,
  url,
  fallback,
  className,
  mediaClassName,
  priority,
  frameTime,
  height,
  width,
  onLoad,
  fill,
  sizes,
  boost,
  format,
  disableVideo,
}: BannerMediaProps) {
  const renderMedia = () => {
    const mediaUrl = url || fallback

    if (mediaUrl) {
      const mediaVariant = extractMediaVariant(mediaUrl)
      const datUriVariant = extractMediaVariantFromDataUri(mediaUrl)
      const variant = mediaVariant || datUriVariant
      if (variant?.type === "video") {
        return (
          <VideoWithControl
            className={classNames(
              "size-full object-cover object-center",
              mediaClassName,
            )}
            format={variant.format}
            poster={fallback ? fallback : undefined}
            shouldPlay={!disableVideo}
            src={mediaUrl}
          />
        )
      }

      return (
        <>
          {url ? (
            <Image
              boost={boost}
              className={classNames(
                "size-full object-cover object-center",
                mediaClassName,
              )}
              fill={fill}
              format={format}
              frameTime={frameTime}
              height={height}
              onLoad={onLoad}
              priority={priority}
              sizes={sizes}
              src={mediaUrl}
              width={width}
            />
          ) : null}
          {
            /*
            Blow up and blur the image if it's a fallback to create a gradient effect
          */
            fallback && !url ? (
              <Image
                boost={boost}
                className={classNames(
                  "size-full object-cover object-center blur-3xl",
                  "*:!pointer-events-none *:*:*:pointer-events-none *:*:pointer-events-none",
                  mediaClassName,
                )}
                fill={fill}
                format={format}
                frameTime={1}
                height={height}
                sizes={sizes}
                src={mediaUrl}
                width={width}
              />
            ) : null
          }
        </>
      )
    }

    return undefined
  }

  return (
    <div
      className={classNames("relative size-full", blur && "blur-xl", className)}
    >
      {renderMedia()}
      {overlay}
    </div>
  )
}

function VideoWithControl({
  src,
  format,
  poster,
  className,
  shouldPlay = true,
}: {
  src: string
  format: string
  poster?: string
  className?: string
  shouldPlay?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!videoRef.current) {
      return
    }

    if (shouldPlay) {
      videoRef.current.play().catch(() => {
        // Ignore play errors (e.g., user hasn't interacted with page yet)
      })
    } else {
      videoRef.current.pause()
    }
  }, [shouldPlay])

  return (
    <Video
      autoPlay={shouldPlay}
      className={className}
      format={format as VideoFormat}
      loop
      muted
      playsInline
      poster={poster}
      ref={videoRef}
      src={src}
    />
  )
}

export function BannerMediaOverlay() {
  return (
    <>
      <div className="dark absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
      <Media lessThan="lg">
        <div className="absolute inset-0 bg-gradient-to-b from-bg-app via-transparent to-bg-app" />
      </Media>
    </>
  )
}

export { BannerMedia }
